import { useEffect, useMemo, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import { nextListFilterToClear } from "@/lib/search-clear-filters";
import type { SearchFilters, SearchPageParams } from "./types";
import {
  createInitialFiltersFromParams,
  toCategorySlug,
  toSlug,
} from "./url-utils";

type FilterUpdates = Record<string, string | boolean | string[] | undefined>;

const MULTI_SELECT_FILTER_KEYS = [
  "categories",
  "brands",
  "collections",
  "subCategories",
  "sizes",
  "growingMethods",
  "types",
  "strains",
  "dominantTerpenes",
  "flavors",
  "provincesOfOrigin",
  "regionsOfProduct",
  "extractionProcesses",
  "carrierOils",
] as const;

const STRING_FILTER_KEYS = [
  "q",
  "strainType",
  "onSale",
  "thcMin",
  "thcMax",
  "cbdMin",
  "cbdMax",
  "cbnMin",
  "cbnMax",
  "cbcMin",
  "cbcMax",
  "thcvMin",
  "thcvMax",
  "piecesMin",
  "piecesMax",
  "totalTerpeneMin",
  "totalTerpeneMax",
  "priceMin",
  "priceMax",
] as const;

const serializeListParam = (key: string, values: string[]) => {
  if (values.length === 0) return null;
  return `${key}=${values.map((value) => toSlug(value)).join(",")}`;
};

export function useSearchFilters(
  initialParams: SearchPageParams,
  isDesktop: boolean
) {
  const router = useRouter();
  // Last URL handed to router.replace, so a path change is not re-issued while
  // the route swap settles. Cleared as soon as a same-path update happens.
  const lastNavigatedUrlRef = useRef<string | null>(null);

  const [filters, setFilters] = useState<SearchFilters>(() =>
    createInitialFiltersFromParams(initialParams)
  );
  const [hasMatchedSlugs, setHasMatchedSlugs] = useState(false);

  const [includeOutOfStock, setIncludeOutOfStock] = useState(() => {
    const value = String(
      (initialParams as SearchPageParams)?.includeOutOfStock || "false"
    ).toLowerCase();
    return value === "true";
  });

  /* Adopt the URL when a navigation comes from outside this hook.

     Normally the URL is a projection of `filters`: the effect further down
     writes it with replaceState, deliberately avoiding a router round-trip so
     toggling a filter causes no refetch or scroll jump. That covers
     filters -> URL. Nothing covered URL -> filters.

     So any navigation this hook did not initiate left the results stale. The
     header mega-menu linking to /search?category=topical, a footer category
     link, moving between two /collections/[handle] routes: Next keeps the same
     component mounted for all of these, `filters` still held its first-mount
     value, and the address bar changed while the products on screen did not.

     Safe against looping with the writer effect: replaceState does not go
     through the router, so `initialParams` only ever changes on a real
     navigation. The serialized key means an unchanged object identity from
     `use(searchParams)` re-rendering does not count as a change. */
  const incomingParamsKey = useMemo(
    () => JSON.stringify(initialParams ?? {}),
    [initialParams]
  );
  const adoptedParamsKeyRef = useRef(incomingParamsKey);

  useEffect(() => {
    if (adoptedParamsKeyRef.current === incomingParamsKey) return;
    adoptedParamsKeyRef.current = incomingParamsKey;
    // The pending-navigation guard belongs to the URL we just left.
    lastNavigatedUrlRef.current = null;

    setFilters(createInitialFiltersFromParams(initialParams));
    setIncludeOutOfStock(
      String(
        (initialParams as SearchPageParams)?.includeOutOfStock || "false"
      ).toLowerCase() === "true"
    );
    setHasMatchedSlugs(false);
  }, [incomingParamsKey, initialParams]);

  useEffect(() => {
    const desired = isDesktop ? 32 : 16;
    setFilters((prev) => {
      if (prev.limit === desired) return prev;
      return { ...prev, limit: desired, page: 1 };
    });
  }, [isDesktop]);

  const updateFilters = (updates: FilterUpdates) => {
    setFilters((prev) => {
      const next = { ...prev, ...updates };

      for (const key of STRING_FILTER_KEYS) {
        if (key in updates) {
          next[key] = typeof updates[key] === "string" ? updates[key] : "";
        }
      }

      if ("sortBy" in updates && typeof updates.sortBy === "string") {
        next.sortBy = updates.sortBy;
      }

      for (const key of MULTI_SELECT_FILTER_KEYS) {
        if (key in updates) {
          next[key] = Array.isArray(updates[key]) ? updates[key] : [];
        }
      }

      next.page = 1;
      return next;
    });
  };

  const clearAllFilters = () => {
    setFilters((prev) => ({
      ...prev,
      q: "",
      categories: [],
      brands: [],
      collections: [],
      subCategories: [],
      sizes: [],
      growingMethods: [],
      types: [],
      strains: [],
      dominantTerpenes: [],
      flavors: [],
      provincesOfOrigin: [],
      regionsOfProduct: [],
      extractionProcesses: [],
      carrierOils: [],
      strainType: "",
      onSale: "",
      thcMin: "",
      thcMax: "",
      cbdMin: "",
      cbdMax: "",
      cbnMin: "",
      cbnMax: "",
      cbcMin: "",
      cbcMax: "",
      thcvMin: "",
      thcvMax: "",
      piecesMin: "",
      piecesMax: "",
      totalTerpeneMin: "",
      totalTerpeneMax: "",
      priceMin: "",
      priceMax: "",
      sortBy: "recommended",
      page: 1,
    }));
  };

  const clearLastFilter = () => {
    setFilters((prev) => {
      const next = { ...prev };
      const clearFirstList = (key: (typeof MULTI_SELECT_FILTER_KEYS)[number]) => {
        if (next[key].length > 0) {
          next[key] = next[key].slice(0, -1);
          return true;
        }
        return false;
      };

      // Ordering lives in lib/search-clear-filters.ts so it can be unit tested.
      const listKey = nextListFilterToClear(next);
      if (listKey && clearFirstList(listKey)) return { ...next, page: 1 };

      if (next.strainType) return { ...next, strainType: "", page: 1 };
      if (next.onSale) return { ...next, onSale: "", page: 1 };
      for (const key of [
        "totalTerpeneMax",
        "totalTerpeneMin",
        "piecesMax",
        "piecesMin",
        "cbnMax",
        "cbnMin",
        "cbcMax",
        "cbcMin",
        "thcvMax",
        "thcvMin",
        "cbdMax",
        "cbdMin",
        "thcMax",
        "thcMin",
        "priceMax",
        "priceMin",
      ] as const) {
        if (next[key]) return { ...next, [key]: "", page: 1 };
      }
      if (next.q) return { ...next, q: "", page: 1 };
      return next;
    });
  };

  useEffect(() => {
    if (typeof window === "undefined") return;

    const params: string[] = [];
    const encodeValue = (value: string) => encodeURIComponent(value);

    if (filters.q) params.push(`q=${encodeValue(filters.q)}`);
    if (filters.categories.length > 0) {
      params.push(
        `category=${filters.categories.map((cat) => toCategorySlug(cat)).join(",")}`
      );
    }

    // Canonical URL form: on /collections/[handle] the path itself carries the
    // collection, so that handle is never repeated in the query string; if the
    // route's collection gets removed from the filters, the page demotes to
    // /search (keeping the rest of the query) so the path never lies.
    const pathname = window.location.pathname;
    const pathHandleMatch = pathname.match(/^\/collections\/([^/]+)\/?$/);
    const pathHandle = pathHandleMatch ? decodeURIComponent(pathHandleMatch[1]) : null;
    const collectionSlugs = (filters.collections || []).map((value) => toSlug(value));
    const pathHandleActive = pathHandle != null && collectionSlugs.includes(toSlug(pathHandle));
    const serializableCollections = pathHandleActive
      ? (filters.collections || []).filter((value) => toSlug(value) !== toSlug(pathHandle!))
      : filters.collections || [];
    const targetPath = pathHandle && !pathHandleActive ? "/search" : pathname;

    const listParams = [
      serializeListParam("brand", filters.brands || []),
      serializeListParam("collection", serializableCollections),
      serializeListParam("subCategory", filters.subCategories || []),
      serializeListParam("size", filters.sizes || []),
      serializeListParam("growingMethod", filters.growingMethods || []),
      serializeListParam("type", filters.types || []),
      serializeListParam("strain", filters.strains || []),
      serializeListParam("dominantTerpene", filters.dominantTerpenes || []),
      serializeListParam("flavor", filters.flavors || []),
      serializeListParam("provinceOfOrigin", filters.provincesOfOrigin || []),
      serializeListParam("regionOfProduct", filters.regionsOfProduct || []),
      serializeListParam("extractionProcess", filters.extractionProcesses || []),
      serializeListParam("carrierOil", filters.carrierOils || []),
    ].filter(Boolean) as string[];
    params.push(...listParams);

    if (filters.strainType) params.push(`strainType=${toSlug(filters.strainType)}`);
    if (filters.onSale) params.push(`onSale=true`);

    for (const key of [
      "thcMin",
      "thcMax",
      "cbdMin",
      "cbdMax",
      "cbnMin",
      "cbnMax",
      "cbcMin",
      "cbcMax",
      "thcvMin",
      "thcvMax",
      "piecesMin",
      "piecesMax",
      "totalTerpeneMin",
      "totalTerpeneMax",
      "priceMin",
      "priceMax",
    ] as const) {
      if (filters[key]) params.push(`${key}=${encodeValue(filters[key])}`);
    }

    if (filters.sortBy && filters.sortBy !== "recommended") {
      params.push(`sortBy=${encodeValue(filters.sortBy)}`);
    }
    if (filters.page > 1) params.push(`page=${filters.page}`);
    if (filters.limit) params.push(`limit=${filters.limit}`);

    const newQueryString = params.join("&");
    const targetUrl = `${targetPath}${newQueryString ? `?${newQueryString}` : ""}`;
    const currentUrl = `${window.location.pathname}${window.location.search}`;
    if (targetUrl === currentUrl) return;

    /* Two different kinds of URL change, and they need different tools.
       A query-string edit (a filter toggled while the path stays put) is the
       common case and happens on nearly every interaction, so it uses
       replaceState: no re-render, no refetch, no scroll jump.

       A PATH change is not the same thing. It only happens when the collection
       that owns a /collections/[handle] route is removed from the filters, and
       the block above demotes the page to /search. replaceState cannot do that
       job: it rewrites the address bar without telling the Next router, so the
       CollectionPage component stays mounted, keeps its `handle` param, and
       keeps rendering collection chrome under a URL that now claims to be
       /search. "Clear last filter" on /collections/cartridges landed exactly
       there — the URL changed, the page did not, and a reload went somewhere
       different from what was on screen. Hand path changes to the router so
       the route actually unmounts. */
    if (targetPath !== window.location.pathname) {
      // Guard against firing the same navigation twice: the effect re-runs once
      // more after the route swap while filter state settles.
      if (lastNavigatedUrlRef.current === targetUrl) return;
      lastNavigatedUrlRef.current = targetUrl;
      router.replace(targetUrl, { scroll: false });
      return;
    }

    lastNavigatedUrlRef.current = null;
    window.history.replaceState({}, "", targetUrl);
  }, [
    filters.q,
    (filters.categories || []).join(","),
    (filters.brands || []).join(","),
    (filters.collections || []).join(","),
    (filters.subCategories || []).join(","),
    (filters.sizes || []).join(","),
    (filters.growingMethods || []).join(","),
    (filters.types || []).join(","),
    (filters.strains || []).join(","),
    (filters.dominantTerpenes || []).join(","),
    (filters.flavors || []).join(","),
    (filters.provincesOfOrigin || []).join(","),
    (filters.regionsOfProduct || []).join(","),
    (filters.extractionProcesses || []).join(","),
    (filters.carrierOils || []).join(","),
    filters.strainType,
    filters.onSale,
    filters.thcMin,
    filters.thcMax,
    filters.cbdMin,
    filters.cbdMax,
    filters.cbnMin,
    filters.cbnMax,
    filters.cbcMin,
    filters.cbcMax,
    filters.thcvMin,
    filters.thcvMax,
    filters.piecesMin,
    filters.piecesMax,
    filters.totalTerpeneMin,
    filters.totalTerpeneMax,
    filters.priceMin,
    filters.priceMax,
    filters.sortBy,
    filters.page,
    filters.limit,
    // Stable across renders (next/navigation), listed so the dep array stays honest.
    router,
  ]);

  return {
    filters,
    setFilters,
    updateFilters,
    clearAllFilters,
    clearLastFilter,
    hasMatchedSlugs,
    setHasMatchedSlugs,
    includeOutOfStock,
    setIncludeOutOfStock,
  };
}
